/**
 * The engagement vocabulary, shared by the two places that speak it.
 *
 * WHY THIS IS ITS OWN FILE
 * -----------------------------------------------------------------------------
 * `EngagementCard` is parked — off the Overview since slice 36, still exported,
 * still covered by its own assertions. `EngagementTiles` is the row the sends
 * card now draws. Both describe the same mechanism: what an open is, why a
 * count of rows is the wrong number, and which of two opposite biases applies.
 *
 * Duplicating any of that would let the two drift, and the drift would not be
 * visible — both would render, both would look careful, and they would tell a
 * reader different things about the same figure. Half the review findings on
 * this project were sentences, so the sentences live in one place.
 *
 * WHAT IS **NOT** HERE
 * -----------------------------------------------------------------------------
 * No unavailable-reason sentences. Those come down the wire, from
 * `ENGAGEMENT_UNAVAILABLE_MESSAGES` in the contract and from `panel.message`,
 * so the API's audit trail and this screen cannot describe the same absence
 * differently. `tracking_off`'s sentence in particular is chosen by the API,
 * because it depends on whether this deployment hosts pictures — a fact the
 * browser does not have.
 */

import { ENGAGEMENT_CAVEAT_MESSAGES, type EngagementCaveat, type EngagementUnavailable } from '@wend/shared';

/** The reasons a reader can act on, by opening the email integration tab. */
export const ENGAGEMENT_SWITCH_REASONS: readonly EngagementUnavailable[] = [
  'tracking_off',
  'open_tracking_off',
  'click_tracking_off',
];

/**
 * Which sentence explains *this* missing half.
 *
 * The tracked arm carries one array for both halves, so a naive `unavailable[0]`
 * would print "click tracking is off" under a missing open rate. The rule: a
 * reason that names this half wins; otherwise the first reason that names
 * neither half — `no_delivered_messages`, `awaiting_first_events` — applies to
 * both and is used; the *other* half's switch reason is never borrowed.
 */
export function absentReasonFor(
  kind: 'open' | 'click',
  unavailable: readonly EngagementUnavailable[],
): EngagementUnavailable | null {
  const named: EngagementUnavailable = kind === 'open' ? 'open_tracking_off' : 'click_tracking_off';
  if (unavailable.includes(named)) return named;
  return (
    unavailable.find(
      (reason) => reason !== 'open_tracking_off' && reason !== 'click_tracking_off',
    ) ?? null
  );
}

export interface EngagementMetricCopy {
  readonly label: string;
  /** Completes "N of M delivered messages …" — the verb belongs to it. */
  readonly event: string;
  /** Singular; pluralised for the repeat count. */
  readonly totalNoun: string;
  /** Why counting rows instead of messages would be the wrong number. */
  readonly repeatsNote: string;
}

export const ENGAGEMENT_METRIC_COPY: Readonly<Record<'open' | 'click', EngagementMetricCopy>> = {
  open: {
    label: 'Opened',
    event: 'were opened at least once',
    totalNoun: 'open',
    repeatsNote:
      'The rate counts each message once: one person opening a newsletter six times on the train did not open it six times.',
  },
  click: {
    label: 'Clicked',
    event: 'had a link followed at least once',
    totalNoun: 'click',
    repeatsNote:
      'The rate counts each message once, so a reader who followed three links in one message is one click here.',
  },
};

/**
 * Every reason the number above is wrong, said where the number is read.
 *
 * Not collapsible, not a tooltip, not a footnote. The two open caveats point in
 * opposite directions and stating only one of them would be worse than stating
 * neither, because it tells the reader which way to correct a figure that is
 * wrong both ways at once. They are printed for the counts as well as for the
 * percentage: a count of opens is polluted by Apple's pre-fetch exactly as a
 * percentage of them is.
 *
 * **Deduplicated and ordered by the contract's own list**, not by insertion.
 * The two halves share caveats — `tracking_partially_enabled` applies to both —
 * and the parked card, which renders one list per half, can print it twice.
 * One list for the pair cannot.
 */
export function CaveatList({ caveats }: { caveats: readonly EngagementCaveat[] }) {
  if (caveats.length === 0) return null;

  return (
    <ul className="flex flex-col gap-1.5">
      {caveats.map((caveat) => (
        <li key={caveat} className="text-[0.78rem] leading-relaxed text-muted">
          {ENGAGEMENT_CAVEAT_MESSAGES[caveat]}
        </li>
      ))}
    </ul>
  );
}
